import type { ChainId } from '@morpho-org/blue-sdk'
import type { SortByOption } from '@morpho-org/ui-v6/client'

import {
  createSearchParamsCache,
  parseAsArrayOf,
  parseAsBoolean,
  parseAsFloat,
  parseAsInteger,
  parseAsString
} from 'nuqs/server'

import type { TableSettingsState } from '@/helpers/cookieStorage/userSettings/tableSettings/shared'
import type { VaultListInput } from '@/lib/procedures/vaultList.contract'

export interface ExploreVaultsFilters {
  v1: boolean
  assets: string[]
  curators: string[]
  chains: ChainId[]
  search: string
  collateralExposure: string[]
  collateralExclusion: string[]
  totalAssetsUsd_gte: number | null
  totalAssetsUsd_lte: number | null
  liquidityUsd_gte: number | null
  liquidityUsd_lte: number | null
  netApy_gte: number | null
  netApy_lte: number | null
  sorting?: {
    id: string
    desc: boolean
  }
}

export interface VaultsFiltersContext {
  isWalletFilterActive: boolean
}

export const COLUMN_IDS = {
  Deposits: 'deposits',
  Vault: 'vault',
  NetApy: 'netApy',
  PerformanceFee: 'performanceFee',
  ManagementFee: 'managementFee',
  Network: 'network',
  Liquidity: 'liquidity',
  Exposure: 'exposure',
  Curator: 'curator',
  VaultAge: 'vaultAge'
} as const

// Column widths in px, shared by the loaded table and its server skeleton
export const COLUMN_SIZES: Record<(typeof COLUMN_IDS)[keyof typeof COLUMN_IDS], number> = {
  [COLUMN_IDS.Deposits]: 140,
  [COLUMN_IDS.Vault]: 305,
  [COLUMN_IDS.NetApy]: 150,
  [COLUMN_IDS.PerformanceFee]: 112,
  [COLUMN_IDS.ManagementFee]: 112,
  [COLUMN_IDS.Network]: 85,
  [COLUMN_IDS.Liquidity]: 140,
  [COLUMN_IDS.Exposure]: 140,
  [COLUMN_IDS.Curator]: 195,
  [COLUMN_IDS.VaultAge]: 132
}

export type VaultOrderBy = NonNullable<VaultListInput['orderBy']>

export const COLUMN_ID_TO_ORDER_BY: Record<string, VaultOrderBy> = {
  [COLUMN_IDS.Deposits]: 'totalAssetsUsd',
  [COLUMN_IDS.Vault]: 'name',
  [COLUMN_IDS.NetApy]: 'netApy',
  [COLUMN_IDS.PerformanceFee]: 'performanceFee',
  [COLUMN_IDS.ManagementFee]: 'managementFee',
  [COLUMN_IDS.Liquidity]: 'liquidityUsd',
  [COLUMN_IDS.VaultAge]: 'creationTimestamp'
}

export const DEFAULT_ORDER_BY = COLUMN_ID_TO_ORDER_BY[COLUMN_IDS.Deposits]!

export const EXPLORE_VAULTS_COLUMN_LABELS: Record<string, string> = {
  [COLUMN_IDS.Vault]: 'Vault',
  [COLUMN_IDS.Deposits]: 'Deposits',
  [COLUMN_IDS.Liquidity]: 'Liquidity',
  [COLUMN_IDS.Curator]: 'Curator',
  [COLUMN_IDS.Exposure]: 'Exposure',
  [COLUMN_IDS.NetApy]: 'APY',
  [COLUMN_IDS.Network]: 'Network',
  [COLUMN_IDS.PerformanceFee]: 'Performance Fee',
  [COLUMN_IDS.ManagementFee]: 'Management Fee',
  [COLUMN_IDS.VaultAge]: 'Creation Date'
}

export const ROW_HEIGHT_ESTIMATE = 64
export const VIRTUALIZER_OVERSCAN = 10

export const VAULTS_PAGE_SIZE = 35

// Rect assumed before the scroll container is measured (SSR + first client
// render), so the virtualizer emits the first page instead of an empty body.
export const VIRTUALIZER_INITIAL_RECT = {
  width: 0,
  height: VAULTS_PAGE_SIZE * ROW_HEIGHT_ESTIMATE
}

export const VAULTS_INITIAL_COLUMNS_VISIBILITY: Record<string, boolean> = {
  [COLUMN_IDS.PerformanceFee]: false,
  [COLUMN_IDS.ManagementFee]: false,
  [COLUMN_IDS.VaultAge]: false
}

export const VAULTS_INITIAL_SORTING: {
  id: string
  desc: boolean
}[] = [
  {
    id: COLUMN_IDS.Deposits,
    desc: true
  }
]

export const VAULTS_STORAGE_KEY = 'vaults'

export const SORT_BY_OPTIONS: SortByOption<string>[] = [
  { value: COLUMN_IDS.Vault, label: 'Vault name' },
  { value: COLUMN_IDS.Deposits, label: 'Deposits' },
  { value: COLUMN_IDS.Liquidity, label: 'Liquidity' },
  { value: COLUMN_IDS.NetApy, label: 'APY' },
  { value: COLUMN_IDS.PerformanceFee, label: 'Performance Fee' },
  { value: COLUMN_IDS.ManagementFee, label: 'Management Fee' },
  { value: COLUMN_IDS.VaultAge, label: 'Creation Date' }
]
export const EXPLORE_VAULTS_STORAGE_VERSION = 7

export const VAULTS_DEFAULT_TABLE_SETTINGS: TableSettingsState = {
  columnVisibility: VAULTS_INITIAL_COLUMNS_VISIBILITY,
  sorting: VAULTS_INITIAL_SORTING,
  columnFilters: [],
  columnOrder: [],
  version: EXPLORE_VAULTS_STORAGE_VERSION
}

export const VaultsUrlFilterParser = {
  search: parseAsString.withDefault(''),
  chains: parseAsArrayOf(parseAsInteger).withDefault([]),
  assets: parseAsArrayOf(parseAsString).withDefault([]),
  curators: parseAsArrayOf(parseAsString).withDefault([]),
  v1: parseAsBoolean.withDefault(false),
  collateralExposure: parseAsArrayOf(parseAsString).withDefault([]),
  collateralExclusion: parseAsArrayOf(parseAsString).withDefault([]),
  totalAssetsUsd_gte: parseAsFloat,
  totalAssetsUsd_lte: parseAsFloat,
  liquidityUsd_gte: parseAsFloat,
  liquidityUsd_lte: parseAsFloat,
  netApy_gte: parseAsFloat,
  netApy_lte: parseAsFloat
}

export const vaultsUrlFilterCache = createSearchParamsCache(VaultsUrlFilterParser)

/** True when anything filters the default V2-only list, including `v1: true`. */
export function hasActiveVaultsFilters(
  filters: ExploreVaultsFilters,
  { isWalletFilterActive }: VaultsFiltersContext
): boolean {
  return (
    !!filters.search ||
    filters.assets.length > 0 ||
    filters.curators.length > 0 ||
    filters.chains.length > 0 ||
    filters.v1 ||
    isWalletFilterActive ||
    filters.collateralExposure.length > 0 ||
    filters.collateralExclusion.length > 0 ||
    filters.totalAssetsUsd_gte != null ||
    filters.totalAssetsUsd_lte != null ||
    filters.liquidityUsd_gte != null ||
    filters.liquidityUsd_lte != null ||
    filters.netApy_gte != null ||
    filters.netApy_lte != null
  )
}

function withVaultsFiltersCleared(
  filters: ExploreVaultsFilters,
  v1: boolean
): ExploreVaultsFilters {
  return {
    ...filters,
    search: '',
    assets: [],
    curators: [],
    chains: [],
    v1,
    collateralExposure: [],
    collateralExclusion: [],
    totalAssetsUsd_gte: null,
    totalAssetsUsd_lte: null,
    liquidityUsd_gte: null,
    liquidityUsd_lte: null,
    netApy_gte: null,
    netApy_lte: null
  }
}

/**
 * "Show only Vaults V2", the same baseline the Advanced panel's Reset restores.
 * Only for actions the user pressed.
 */
export function clearVaultsFilters(filters: ExploreVaultsFilters): ExploreVaultsFilters {
  return withVaultsFiltersCleared(filters, false)
}

/**
 * The state a fresh visit arrives in, minus the version choice. Used when a wallet change
 * drops wallet-tied filters: housekeeping, not a request to change which vault versions
 * are listed, so `v1` is carried over untouched.
 */
export function resetVaultsFiltersToInitial(filters: ExploreVaultsFilters): ExploreVaultsFilters {
  return withVaultsFiltersCleared(filters, filters.v1)
}

/** The `version` the vaults API is asked for. */
export function deriveVaultsVersion(v1: boolean): '2.0' | undefined {
  return v1 ? undefined : '2.0'
}
